import { defaultApi } from '@/api';
import { LinkedColumn } from '@/features/canvas/types/linkage';

export interface CsvUploadResponse {
  import_key: string;
  filename: string;
  headers: string[];
  row_count: number;
  preview: Record<string, string>[];
}

export interface CancelImportResponse {
  message: string;
}

export interface KonexisField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'select' | 'currency';
  required?: boolean;
  options?: string[];
}

export interface AvailableFieldsResponse {
  fields: KonexisField[];
}

export interface MappingTemplate {
  id: number;
  name: string;
  mappings: LinkedColumn[];
  created_at: string;
}

export interface MappingTemplatesResponse {
  templates: MappingTemplate[];
}

export interface CreateTemplateResponse {
  template: MappingTemplate;
}

export interface DeleteTemplateResponse {
  message: string;
}

export interface MatchInitiativesResponse {
  matched: number[];
  not_found: number[];
  matched_details: Array<{
    id: number;
    title: string;
  }>;
}

/**
 * Upload CSV file for import
 */
export async function uploadCsvForImport(roadmapId: number, file: File): Promise<CsvUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await defaultApi.post<CsvUploadResponse>(
    `/roadmaps/${roadmapId}/import/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
}

/**
 * Cancel import and clear cached data
 */
export async function cancelImport(roadmapId: number, importKey: string): Promise<CancelImportResponse> {
  const { data } = await defaultApi.post<CancelImportResponse>(
    `/roadmaps/${roadmapId}/import/cancel`,
    { import_key: importKey }
  );
  return data;
}

/**
 * Get available fields for mapping CSV columns
 */
export async function getAvailableFields(roadmapId: number): Promise<KonexisField[]> {
  const { data } = await defaultApi.get<AvailableFieldsResponse>(
    `/roadmaps/${roadmapId}/import/fields`
  );
  return data.fields;
}

/**
 * List all import mapping templates for a roadmap
 */
export async function listMappingTemplates(roadmapId: number): Promise<MappingTemplate[]> {
  const { data } = await defaultApi.get<MappingTemplatesResponse>(
    `/roadmaps/${roadmapId}/import/templates`
  );
  return data.templates;
}

/**
 * Create a new import mapping template
 */
export async function createMappingTemplate(
  roadmapId: number,
  name: string,
  mappings: LinkedColumn[]
): Promise<MappingTemplate> {
  const { data } = await defaultApi.post<CreateTemplateResponse>(
    `/roadmaps/${roadmapId}/import/templates`,
    { name, mappings }
  );
  return data.template;
}

/**
 * Update an existing import mapping template
 */
export async function updateMappingTemplate(
  roadmapId: number,
  templateId: number,
  name: string,
  mappings: LinkedColumn[]
): Promise<MappingTemplate> {
  const { data } = await defaultApi.put<CreateTemplateResponse>(
    `/roadmaps/${roadmapId}/import/templates/${templateId}`,
    { name, mappings }
  );
  return data.template;
}

/**
 * Delete an import mapping template
 */
export async function deleteMappingTemplate(roadmapId: number, templateId: number): Promise<void> {
  await defaultApi.delete<DeleteTemplateResponse>(
    `/roadmaps/${roadmapId}/import/templates/${templateId}`
  );
}

/**
 * Match initiative IDs to check which exist in the roadmap
 */
export async function matchInitiatives(
  roadmapId: number,
  ids: number[]
): Promise<MatchInitiativesResponse> {
  const { data } = await defaultApi.post<MatchInitiativesResponse>(
    `/roadmaps/${roadmapId}/import/match`,
    { ids }
  );
  return data;
}